import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  Mail24Regular,
  Settings24Regular,
} from '@fluentui/react-icons';
import { SmtpConfigModal } from '../../components/Alerts/SmtpConfigModal';
import { ThresholdConfigModal } from '../../components/Alerts/ThresholdConfigModal';
import { EmailRecipientsSection } from '../../components/Alerts/EmailRecipientsSection';
import { AlertsHistoryTable } from '../../components/Alerts/AlertsHistoryTable';
import { alertsService } from '../../services/alertsService';

const useStyles = makeStyles({
  container: {
    paddingTop: '24px',
    paddingBottom: '24px',
    paddingLeft: '32px',
    paddingRight: '32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
  },
  pageHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  breadcrumb: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
  },
  pageTitle: {
    margin: 0,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '-0.01em',
    color: tokens.colorNeutralForeground1,
  },
  pageHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '12px',
    paddingRight: '12px',
    borderRadius: '999px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    ...shorthands.border('1px', 'solid', 'transparent'),
  },
  pillDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  pillActiveAlerts: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    color: tokens.colorPaletteYellowForeground1,
    ...shorthands.borderColor(tokens.colorPaletteYellowBorder1),
  },
  pillActiveAlertsDot: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
  },
  pillNoAlerts: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
    ...shorthands.borderColor(tokens.colorPaletteGreenBorder1),
  },
  pillNoAlertsDot: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
  },
  pillSmtpOk: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
    ...shorthands.borderColor(tokens.colorPaletteGreenBorder1),
  },
  pillSmtpOkDot: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
  },
  pillSmtpWarn: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    color: tokens.colorPaletteYellowForeground1,
    ...shorthands.borderColor(tokens.colorPaletteYellowBorder1),
  },
  pillSmtpWarnDot: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  divider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
});

export const AlertsPage: React.FC = () => {
  const styles = useStyles();

  // Modal states
  const [smtpModalOpen, setSmtpModalOpen] = useState(false);
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);

  // Data states
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [activeAlertCount, setActiveAlertCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [smtpConfig, alertCount] = await Promise.all([
        alertsService.getSmtpConfig(),
        alertsService.getActiveAlertCount()
      ]);

      setSmtpConfigured(smtpConfig.isConfigured && smtpConfig.isEnabled);
      setActiveAlertCount(alertCount);
    } catch (err) {
      console.error('Error loading alerts data:', err);
      setError('Erro ao carregar dados de alertas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleSmtpSaved = useCallback(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleThresholdSaved = useCallback(() => {
    // sem efeito agora
  }, []);

  const handleRecipientsChanged = useCallback(() => {
    // Filho gerencia o próprio reload da lista; nada a fazer aqui no momento.
  }, []);

  const handleAlertResolved = useCallback(() => {
    // Atualiza apenas a contagem de alertas ativos no header
    loadInitialData();
  }, [loadInitialData]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Carregando configurações de alertas..." />
      </div>
    );
  }

  const hasActiveAlerts = activeAlertCount > 0;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <span className={styles.breadcrumb}>Monitor &nbsp;/&nbsp; Alertas</span>
          <h1 className={styles.pageTitle}>Configuração de Alertas</h1>
        </div>

        <div className={styles.pageHeaderRight}>
          <span className={`${styles.pill} ${hasActiveAlerts ? styles.pillActiveAlerts : styles.pillNoAlerts}`}>
            <span className={`${styles.pillDot} ${hasActiveAlerts ? styles.pillActiveAlertsDot : styles.pillNoAlertsDot}`} />
            {hasActiveAlerts
              ? `${activeAlertCount} alerta${activeAlertCount > 1 ? 's' : ''} ativo${activeAlertCount > 1 ? 's' : ''}`
              : 'Nenhum alerta ativo'}
          </span>
          <span className={`${styles.pill} ${smtpConfigured ? styles.pillSmtpOk : styles.pillSmtpWarn}`}>
            <span className={`${styles.pillDot} ${smtpConfigured ? styles.pillSmtpOkDot : styles.pillSmtpWarnDot}`} />
            {smtpConfigured ? 'SMTP configurado' : 'SMTP não configurado'}
          </span>
          <Button
            appearance="secondary"
            icon={<Mail24Regular />}
            onClick={() => setSmtpModalOpen(true)}
          >
            SMTP
          </Button>
          <Button
            appearance="primary"
            icon={<Settings24Regular />}
            onClick={() => setThresholdModalOpen(true)}
          >
            Thresholds
          </Button>
        </div>
      </header>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Recipients (card-resumo + modal de gerenciamento) */}
      <EmailRecipientsSection
        onChanged={handleRecipientsChanged}
      />

      {/* Alert History */}
      <AlertsHistoryTable
        onAlertResolved={handleAlertResolved}
      />

      {/* Modals */}
      <SmtpConfigModal
        open={smtpModalOpen}
        onClose={() => setSmtpModalOpen(false)}
        onSaved={handleSmtpSaved}
      />

      <ThresholdConfigModal
        open={thresholdModalOpen}
        onClose={() => setThresholdModalOpen(false)}
        onSaved={handleThresholdSaved}
      />
    </div>
  );
};

export default AlertsPage;
